import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  IconButton,
} from '@chakra-ui/react';
import { FiSearch, FiRefreshCw, FiPlus, FiMail, FiSettings, FiCalendar } from 'react-icons/fi';
import { BsFillPlayFill, BsFillPauseFill, BsFillStopFill } from 'react-icons/bs';

import { getAuth, postAuth, AuthError } from '../../util/APIHelp';
import { getNameFromToken } from '../../util/IsAuth';
import { Error } from '../error/Error';

interface ShippingWindow {
  name: string;
  startDay: string;
  startTime: string;
  endDay: string;
  endTime: string;
  shippingDay: string;
}

interface OrderReference {
  wcOrderId: string;
  emailRecipients: string;
  createdDate: string;
}

interface ManualOrderId {
  id: string;
  date: string;
  time: string;
}

const EmailAutomationPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);

  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  const [automationStatus, setAutomationStatus] = useState('Not started');
  const [controlsLoading, setControlsLoading] = useState(false);
  const [controlsError, setControlsError] = useState<string | null>(null);

  const [shippingWindows, setShippingWindows] = useState<ShippingWindow[]>([]);
  const [shippingWindowsLoading, setShippingWindowsLoading] = useState(false);
  const [shippingWindowsError, setShippingWindowsError] = useState<string | null>(null);

  const [orderRefs, setOrderRefs] = useState<OrderReference[]>([]);
  const [filteredOrderRefs, setFilteredOrderRefs] = useState<OrderReference[]>([]); // New state for filtered results
  const [orderRefsLoading, setOrderRefsLoading] = useState(false);
  const [orderRefsError, setOrderRefsError] = useState<string | null>(null);
  const [searchOrderId, setSearchOrderId] = useState('');

  const [isManualSendModalOpen, setIsManualSendModalOpen] = useState(false);
  const [manualOrderIdInput, setManualOrderIdInput] = useState('');
  const [manualDateInput, setManualDateInput] = useState('');
  const [manualTimeInput, setManualTimeInput] = useState('');
  const [manualOrderIdsToSubmit, setManualOrderIdsToSubmit] = useState<ManualOrderId[]>([]);
  const [manualSendError, setManualSendError] = useState<string | null>(null);
  const [manualSendLoading, setManualSendLoading] = useState(false);


  const bgColor = useColorModeValue('gray.100', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'whiteAlpha.900');
  const tableHeaderBg = useColorModeValue('gray.200', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const orangeColor = useColorModeValue('orange.400', 'orange.400');
  const gray300Color = useColorModeValue('gray.300', 'gray.300');


  const fetchAutomationStatus = async () => {
    setControlsLoading(true);
    setControlsError(null);
    try {
      const response = await getAuth("/automation/email/status");
      if (response.ok) {
        const statusText = await response.text();
        setAutomationStatus(statusText);
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        setControlsError(`Failed to fetch status: ${response.status} - ${errorText}`);

        if (response.status === 401 || response.status === 403) {
          setPageError(`You do not have permission to view automation controls: ${errorText}`);
        }
      }
    } catch (error) {
      if(error instanceof AuthError) {
        setControlsError(error.message);
        setPageError(error.message);
      } else if (error instanceof Error) {
        setControlsError(String(error));
        setPageError(String(error));
      } else {
        const unexpectedError = "An unexpected error occured while fetching automation status."
        setControlsError(unexpectedError);
        setPageError(unexpectedError);
      }
    } finally {
      setControlsLoading(false);
    }
  };

  const handleControlAction = async (action: 'start' | 'stop' | 'pause') => {
    setControlsLoading(true);
    setControlsError(null);
    try {
      const response = await postAuth(`/automation/email/${action}`, {});
      if (response.ok) {
        await fetchAutomationStatus();
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        setControlsError(`Failed to ${action} automation: ${response.status} - ${errorText}`);
        if (response.status === 401 || response.status === 403) {
          setPageError(`You do not have permission to perform this action: ${errorText}`);
        }
      }
    } catch (error) {
      if(error instanceof AuthError) {
        setControlsError(error.message);
        setPageError(error.message);
      } else if (error instanceof Error) {
        setControlsError(String(error));
        setPageError(String(error));
      } else {
        const unexpectedError = "An unexpected error occured while handling email automation control action."
        setControlsError(unexpectedError);
        setPageError(unexpectedError);
      }

    } finally {
      setControlsLoading(false);
    }
  };

  const fetchShippingWindows = async () => {
    setShippingWindowsLoading(true);
    setShippingWindowsError(null);
    try {
      const response = await getAuth("/automation/email/shipping-windows");
      if (response.ok) {
        const data = await response.json();
        setShippingWindows(data);
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        setShippingWindowsError(`Failed to fetch shipping windows: ${response.status} - ${errorText}`);
        if (response.status === 401 || response.status === 403) {
          setPageError(`You do not have permission to view shipping windows: ${errorText}`);
        }
      }
    } catch (error) { // Reverted type to 'any' or 'unknown' for direct check
      if(error instanceof AuthError) {
        setShippingWindowsError(error.message);
        setPageError(error.message);
      } else if (error instanceof Error) {
        setShippingWindowsError(String(error));
        setPageError(String(error));
      } else {
        const unexpectedError = "An unexpected error occured while fetching shipping windows."
        setShippingWindowsError(unexpectedError);
        setPageError(unexpectedError);
      }
    } finally {
      setShippingWindowsLoading(false);
    }
  };

  const fetchOrderRefs = async () => {
    setOrderRefsLoading(true);
    setOrderRefsError(null);
    try {
      const response = await getAuth("/automation/email/order-references");
      if (response.ok) {
        const data = await response.json();
        setOrderRefs(data);
        setFilteredOrderRefs(data); // Initialize filtered list with all data
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        setOrderRefsError(`Failed to fetch email log: ${response.status} - ${errorText}`);
        if (response.status === 401 || response.status === 403) {
          setPageError(`You do not have permission to view email logs: ${errorText}`);
        }
      }
    } catch (error) { 
      if(error instanceof AuthError) {
        setOrderRefsError(error.message);
        setPageError(error.message);
      } else if (error instanceof Error) {
        setOrderRefsError(String(error));
        setPageError(String(error));
      } else {
        const unexpectedError = "An unexpected error occured while fetching order refs."
        setOrderRefsError(unexpectedError);
        setPageError(unexpectedError);
      }
      
    } finally {
      setOrderRefsLoading(false);
    }
  };

  const handleSearch = () => {
  if (searchOrderId.trim() === '') {
    setFilteredOrderRefs(orderRefs);
  } else {
    const filtered = orderRefs.filter(ref => {
      // Safely convert ref.wcOrderId to a string before calling toLowerCase()
      const wcOrderIdAsString = String(ref.wcOrderId).toLowerCase();
      return wcOrderIdAsString.includes(searchOrderId.toLowerCase());
    });
    setFilteredOrderRefs(filtered);
  }
};


  //TODO not supported currently by backend
  const handleAddManualOrderId = () => {
    if (manualOrderIdInput.trim() && manualDateInput && manualTimeInput) {
      setManualOrderIdsToSubmit([
        ...manualOrderIdsToSubmit,
        { id: manualOrderIdInput.trim(), date: manualDateInput, time: manualTimeInput }
      ]);
      setManualOrderIdInput('');
      setManualDateInput('');
      setManualTimeInput('');
      setManualSendError(null);
    } else {
      setManualSendError('Please enter an Order ID, Date, and Time.');
    }
  };

  const handleSubmitManualOrderIds = async () => {
    if (manualOrderIdsToSubmit.length === 0) {
      setManualSendError('No Order IDs to submit.');
      return;
    }

    setManualSendLoading(true);
    setManualSendError(null);
    try {
      const payload = manualOrderIdsToSubmit.map(item => ({
        orderId: item.id,
        scheduledDateTime: `${item.date}T${item.time}:00`
      }));
      const response = await postAuth("/automation/email/manual-send", payload);

      if (response.ok) {
        setManualOrderIdsToSubmit([]);
        setIsManualSendModalOpen(false);
        await fetchOrderRefs();
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        setManualSendError(`Failed to send manually: ${response.status} - ${errorText}`);
        if (response.status === 401 || response.status === 403) {
          setPageError(`You do not have permission to manually send emails: ${errorText}`);
        }
      }
    } catch (err) { // Reverted type to 'any' or 'unknown' for direct check
      setManualSendError(`Network error: ${err instanceof Error ? err : String(err)}`);
      setPageError(`Network error during manual send: ${err instanceof Error ? err : String(err)}`);
    } finally {
      setManualSendLoading(false);
    }
  };

  // --- Initial Data Fetch on Component Mount ---
  useEffect(() => {
    const initializePageData = async () => {
      setPageLoading(true);
      setPageError(null); // Clear any previous page-level errors

      await fetchAutomationStatus();
      await fetchShippingWindows();
      await fetchOrderRefs();

      setPageLoading(false);
    };

    initializePageData();
  }, []);


  // --- Conditional Rendering for Page-Level Loading/Error ---
  if (pageLoading) {
    return (
      <Center minH="100vh" bg={bgColor}>
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color={textColor}>Loading Email Automation page...</Text>
        </VStack>
      </Center>
    );
  }

  if (pageError) {
    return <Error errorMessage={pageError} />;
  }

  return (
    <Box p={4} bg={bgColor}>
      <VStack align="stretch" spacing={6}>
        <Heading as="h1" size="xl" color={textColor} mb={4}>
          Email Automation
        </Heading>

        <Tabs index={tabIndex} onChange={(index) => setTabIndex(index)} variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab _selected={{ color: 'white', bg: 'blue.500' }}>
              <HStack>
                <FiSettings /> <Text>Controls</Text>
              </HStack>
            </Tab>
            <Tab _selected={{ color: 'white', bg: 'blue.500' }}>
              <HStack>
                <FiCalendar /> <Text>Shipping Windows</Text>
              </HStack>
            </Tab>
            <Tab _selected={{ color: 'white', bg: 'blue.500' }}>
              <HStack>
                <FiMail /> <Text>Email Log</Text>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            {/* Controls Section */}
            <TabPanel p={4} bg={cardBg} borderRadius="md" boxShadow="sm">
              <VStack spacing={4} align="flex-start">
                <Text fontSize="lg" color={textColor}>
                  Status: {controlsLoading ? <Spinner size="sm" /> : automationStatus}
                </Text>
                {controlsError && (
                  <Alert status="error" variant="left-accent" mt={2}>
                    <AlertIcon />
                    {controlsError}
                  </Alert>
                )}
                <HStack spacing={4}>
                  <Button
                    colorScheme="green"
                    onClick={() => handleControlAction('start')}
                    isLoading={controlsLoading}
                    leftIcon={<BsFillPlayFill />}
                  >
                    Start
                  </Button>
                  <Button
                    colorScheme="red"
                    onClick={() => handleControlAction('stop')}
                    isLoading={controlsLoading}
                    leftIcon={<BsFillStopFill />}
                  >
                    Stop
                  </Button>
                  <Button
                    colorScheme="orange"
                    onClick={() => handleControlAction('pause')}
                    isLoading={controlsLoading}
                    leftIcon={<BsFillPauseFill />}
                  >
                    Pause
                  </Button>
                </HStack>
              </VStack>
            </TabPanel>

            {/* Shipping Windows Section */}
            <TabPanel p={4} bg={cardBg} borderRadius="md" boxShadow="sm">
              <Heading as="h3" size="md" mb={4} color={textColor}>
                Shipping Windows
              </Heading>
              {shippingWindowsLoading ? (
                <Center py={10}>
                  <Spinner size="xl" />
                </Center>
              ) : shippingWindowsError ? (
                <Alert status="error" variant="left-accent">
                  <AlertIcon />
                  {shippingWindowsError}
                </Alert>
              ) : (
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        <Th color={textColor}>Window Name</Th>
                        <Th color={textColor}>Start Day</Th>
                        <Th color={textColor}>Start Time</Th>
                        <Th color={textColor}>End Day</Th>
                        <Th color={textColor}>End Time</Th>
                        <Th color={textColor}>Shipping Day</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {shippingWindows.length > 0 ? (
                        shippingWindows.map((window, index) => (
                          <Tr key={index}>
                            <Td color={textColor}>{window.name}</Td>
                            <Td color={textColor}>{window.startDay}</Td>
                            <Td color={textColor}>{window.startTime}</Td>
                            <Td color={textColor}>{window.endDay}</Td>
                            <Td color={textColor}>{window.endTime}</Td>
                            <Td color={textColor}>{window.shippingDay}</Td>
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <Td colSpan={6} textAlign="center" color={textColor}>No shipping windows found.</Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>

            {/* Order ref Section */}
            <TabPanel p={4} bg={cardBg} borderRadius="md" boxShadow="sm">
              <Heading as="h3" size="md" mb={4} color={textColor}>
                Email Order References
              </Heading>
              <HStack spacing={2} mb={4}>
                <InputGroup flex={1}>
                  <Input
                    type="search"
                    placeholder="WC Order ID"
                    value={searchOrderId}
                    onChange={(e) => setSearchOrderId(e.target.value)}
                    bg={inputBg}
                    color={textColor}
                  />
                  <InputLeftElement pointerEvents="none">
                    <FiSearch color={gray300Color} />
                  </InputLeftElement>
                </InputGroup>
                <Button colorScheme="blue" onClick={handleSearch}>
                  Search
                </Button>
                <IconButton
                  aria-label="Refresh Log"
                  icon={<FiRefreshCw />}
                  onClick={() => {
                    setSearchOrderId(''); // Clear search input
                    fetchOrderRefs(); // Fetch all logs
                  }}
                  colorScheme="teal"
                />
                <Button colorScheme="purple" onClick={() => setIsManualSendModalOpen(true)}>
                  Manual
                </Button>
              </HStack>

              {orderRefsLoading ? (
                <Center py={10}>
                  <Spinner size="xl" />
                </Center>
              ) : orderRefsError ? (
                <Alert status="error" variant="left-accent">
                  <AlertIcon />
                  {orderRefsError}
                </Alert>
              ) : (
                <TableContainer maxH="400px" overflowY="auto"> {/* Added maxH and overflowY for scroll */}
                  <Table variant="simple" size="sm">
                    <Thead bg={tableHeaderBg} position="sticky" top="0" zIndex="sticky">
                      <Tr>
                        <Th color={textColor}>WC Order ID</Th>
                        <Th color={textColor}>Email Recipients</Th>
                        <Th color={textColor}>Created Date</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredOrderRefs.length > 0 ? (
                        filteredOrderRefs.map((orderRef, index) => (
                          <Tr key={index}>
                            <Td color={textColor}>{orderRef.wcOrderId}</Td>
                            <Td color={textColor}>{orderRef.emailRecipients}</Td>
                            <Td color={textColor}>{orderRef.createdDate}</Td>
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <Td colSpan={3} textAlign="center" color={textColor}>No email order refs found.</Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Manual Sending Emails Modal */}
      <Modal isOpen={isManualSendModalOpen} onClose={() => setIsManualSendModalOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent bg={cardBg} color={textColor} borderRadius="lg">
          <ModalHeader>Manual Sending Emails</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>Enter in WC Order IDs to be queued for automated email sending.</Text>
              <Text fontSize="sm" color={orangeColor} fontWeight="bold"> {/* Use defined orangeColor */}
                Note: This is only meant for emails that were missed during server shutdown.
              </Text>

              <HStack>
                <Input
                  id="manualOrderIdInput"
                  type="text"
                  placeholder="WC Order ID to add"
                  value={manualOrderIdInput}
                  onChange={(e) => setManualOrderIdInput(e.target.value)}
                  bg={inputBg}
                  color={textColor}
                />
                <Button colorScheme="blue" onClick={handleAddManualOrderId} leftIcon={<FiPlus />}>
                  Add
                </Button>
              </HStack>

              <HStack>
                <FormControl>
                  <FormLabel htmlFor="dateInput">Select Date:</FormLabel>
                  <Input
                    type="date"
                    id="dateInput"
                    value={manualDateInput}
                    onChange={(e) => setManualDateInput(e.target.value)}
                    bg={inputBg}
                    color={textColor}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="timeInput">Select Time:</FormLabel>
                  <Input
                    type="time"
                    id="timeInput"
                    value={manualTimeInput}
                    onChange={(e) => setManualTimeInput(e.target.value)}
                    bg={inputBg}
                    color={textColor}
                  />
                </FormControl>
              </HStack>

              {manualSendError && (
                <Alert status="error" variant="left-accent">
                  <AlertIcon />
                  {manualSendError}
                </Alert>
              )}

              <TableContainer maxH="200px" overflowY="auto" borderWidth="1px" borderRadius="md">
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg} position="sticky" top="0" zIndex="sticky">
                    <Tr>
                      <Th color={textColor}>WC Order ID</Th>
                      <Th color={textColor}>Date</Th>
                      <Th color={textColor}>Time</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {manualOrderIdsToSubmit.length > 0 ? (
                      manualOrderIdsToSubmit.map((item, index) => (
                        <Tr key={index}>
                          <Td color={textColor}>{item.id}</Td>
                          <Td color={textColor}>{item.date}</Td>
                          <Td color={textColor}>{item.time}</Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={3} textAlign="center" color={textColor}>No IDs added yet.</Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleSubmitManualOrderIds}
              isLoading={manualSendLoading}
              mr={3}
            >
              Submit IDs
            </Button>
            <Button variant="ghost" onClick={() => setIsManualSendModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default EmailAutomationPage;